import React, { useState } from 'react';
import { Typography, Box, Link, TextField, Button, Alert, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import emailjs from '@emailjs/browser';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

// ─────────────────────────────────────────────────────────────────────────────
// EmailJS-Konfiguration
// 1. Konto anlegen auf https://www.emailjs.com (kostenlos, 200 Mails/Monat)
// 2. "Add New Service" → dein E-Mail-Konto verbinden (z.B. Gmail/Outlook)
// 3. "Email Templates" → neues Template erstellen mit den Variablen:
//    {{from_name}}, {{from_email}}, {{phone}}, {{message}}
// 4. Die drei Werte unten eintragen:
// ─────────────────────────────────────────────────────────────────────────────
const EMAILJS_SERVICE_ID  = 'service_abc123';
const EMAILJS_TEMPLATE_ID = 'template_r8ea35k';
const EMAILJS_PUBLIC_KEY  = 'KXUvZSruHKVm-XHIz';

const contactItems = [
  {
    icon: PhoneIcon,
    label: 'Telefon',
    value: '+49 15560 759736',
    href: 'tel:+4915560759736',
    color: '#e8f5e9',
    iconColor: '#2E7D32',
  },
  {
    icon: WhatsAppIcon,
    label: 'WhatsApp',
    value: '+49 15560 759736',
    href: 'https://wa.me/4915560759736',
    color: '#e8f5e9',
    iconColor: '#25D366',
  },
  {
    icon: EmailIcon,
    label: 'E-Mail',
    value: 'kontakt@heilpraxis-spiller.de',
    href: 'mailto:kontakt@heilpraxis-spiller.de',
    color: '#fff8e1',
    iconColor: '#D4A843',
  },
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '14px',
    backgroundColor: '#fff',
    '& fieldset': { borderColor: 'rgba(76,175,80,0.25)' },
    '&:hover fieldset': { borderColor: '#4CAF50' },
    '&.Mui-focused fieldset': { borderColor: '#2E7D32' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#2E7D32' },
};

function Kontakt() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '', privacy: false });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          phone: form.phone || '–',
          message: form.message,
        },
        EMAILJS_PUBLIC_KEY,
      );
      setStatus('success');
      setForm({ name: '', email: '', phone: '', message: '', privacy: false });
    } catch (err) {
      console.error('EmailJS Fehler:', err);
      setStatus('error');
    }
  };

  return (
    <Box>
      {/* Header Banner */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1B5E20 0%, #2E7D32 60%, #388E3C 100%)',
          py: { xs: 7, md: 10 },
          px: 3,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{
          position: 'absolute', top: -80, left: -80, width: 350, height: 350,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)',
          pointerEvents: 'none',
        }} />
        <Typography sx={{
          fontFamily: '"Playfair Display", serif',
          fontStyle: 'italic',
          color: '#D4A843',
          fontSize: '1rem',
          letterSpacing: 2,
          textTransform: 'uppercase',
          mb: 1,
        }}>
          Ich freue mich auf Sie
        </Typography>
        <Typography variant="h3" sx={{
          color: '#fff',
          fontFamily: '"Playfair Display", serif',
          fontSize: { xs: '1.8rem', md: '2.5rem' },
        }}>
          Kontakt & Terminvereinbarung
        </Typography>
        <Typography sx={{
          color: 'rgba(255,255,255,0.75)', mt: 2,
          fontSize: { xs: '0.97rem', md: '1.05rem' },
          maxWidth: 500, mx: 'auto',
        }}>
          Die Terminvergabe erfolgt auf Anfrage, telefonisch oder per E-Mail.
        </Typography>
      </Box>

      {/* Content */}
      <Box sx={{ py: { xs: 6, md: 10 }, px: 3, maxWidth: 900, mx: 'auto' }}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 5 }}>

          {/* ── Left: Contact Info ─────────────────────────────── */}
          <Box sx={{ flex: '0 0 auto', width: { xs: '100%', md: 300 } }}>
            <Typography variant="h6" sx={{
              fontFamily: '"Playfair Display", serif',
              color: '#1B5E20', mb: 3,
            }}>
              Direkt erreichbar
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
              {contactItems.map(({ icon: Icon, label, value, href, color, iconColor }) => (
                <Box key={label} sx={{
                  display: 'flex', alignItems: 'center', gap: 2,
                  p: 2, borderRadius: '16px',
                  backgroundColor: '#FDFAF4',
                  border: '1px solid rgba(76,175,80,0.1)',
                  boxShadow: '0 2px 12px rgba(46,125,50,0.06)',
                  transition: 'transform 0.2s ease',
                  '&:hover': { transform: 'translateY(-2px)' },
                }}>
                  <Box sx={{
                    width: 44, height: 44, borderRadius: '12px',
                    backgroundColor: color,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    <Icon sx={{ fontSize: 22, color: iconColor }} />
                  </Box>
                  <Box>
                    <Typography sx={{ color: '#8D7A6B', fontSize: '0.75rem', fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
                      {label}
                    </Typography>
                    <Link href={href}
                      target={href.startsWith('http') ? '_blank' : undefined}
                      rel={href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      sx={{
                        color: '#2C2A27', fontSize: '0.9rem', textDecoration: 'none',
                        '&:hover': { color: '#2E7D32' }, transition: 'color 0.2s',
                      }}>
                      {value}
                    </Link>
                  </Box>
                </Box>
              ))}
            </Box>

            <Box sx={{
              p: 2.5, borderRadius: '16px',
              background: 'linear-gradient(135deg, #e8f5e9, #f0ede4)',
              border: '1px solid rgba(76,175,80,0.15)',
              display: 'flex', gap: 1.5, alignItems: 'flex-start',
            }}>
              <AccessTimeIcon sx={{ color: '#2E7D32', fontSize: 22, mt: 0.2, flexShrink: 0 }} />
              <Typography sx={{ color: '#5C5244', lineHeight: 1.75, fontSize: '0.88rem' }}>
                Termine werden individuell vereinbart. Ich melde mich so schnell wie möglich bei Ihnen.
              </Typography>
            </Box>
          </Box>

          {/* ── Right: Contact Form ────────────────────────────── */}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{
              fontFamily: '"Playfair Display", serif',
              color: '#1B5E20', mb: 3,
            }}>
              Nachricht schreiben
            </Typography>

            {status === 'success' ? (
              <Box sx={{
                p: 4, borderRadius: '20px',
                background: 'linear-gradient(135deg, #e8f5e9, #f0ede4)',
                border: '1px solid rgba(76,175,80,0.2)',
                textAlign: 'center',
              }}>
                <CheckCircleOutlineIcon sx={{ fontSize: 52, color: '#2E7D32', mb: 2 }} />
                <Typography variant="h6" sx={{ fontFamily: '"Playfair Display", serif', color: '#1B5E20', mb: 1 }}>
                  Vielen Dank für Ihre Nachricht!
                </Typography>
                <Typography sx={{ color: '#5C5244', lineHeight: 1.75, fontSize: '0.97rem' }}>
                  Ich habe Ihre Anfrage erhalten und melde mich so bald wie möglich bei Ihnen.
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => setStatus('idle')}
                  sx={{ mt: 3, borderRadius: '30px', borderColor: '#2E7D32', color: '#2E7D32' }}
                >
                  Neue Nachricht
                </Button>
              </Box>
            ) : (
              <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
                  <TextField
                    label="Ihr Name *"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    fullWidth
                    sx={inputSx}
                  />
                  <TextField
                    label="Ihre E-Mail *"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    fullWidth
                    sx={inputSx}
                  />
                </Box>
                <TextField
                  label="Telefon (optional)"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  fullWidth
                  sx={inputSx}
                />
                <TextField
                  label="Ihre Nachricht *"
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  required
                  fullWidth
                  multiline
                  rows={5}
                  sx={inputSx}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      name="privacy"
                      checked={form.privacy}
                      onChange={handleChange}
                      required
                      sx={{
                        color: 'rgba(76,175,80,0.5)',
                        '&.Mui-checked': { color: '#2E7D32' },
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '0.88rem', color: '#5C5244', lineHeight: 1.5 }}>
                      Ich habe die{' '}
                      <Link href="/datenschutzerklarung" sx={{ color: '#2E7D32', fontWeight: 700 }}>
                        Datenschutzerklärung
                      </Link>{' '}
                      gelesen und bin einverstanden, dass meine Angaben zur Bearbeitung meiner Anfrage verwendet werden. *
                    </Typography>
                  }
                />

                {status === 'error' && (
                  <Alert severity="error" sx={{ borderRadius: '12px' }}>
                    Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder schreiben Sie uns direkt per E-Mail.
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  disabled={status === 'sending'}
                  endIcon={status === 'sending' ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
                  sx={{
                    alignSelf: 'flex-start',
                    borderRadius: '30px',
                    px: 5, py: 1.4,
                    fontSize: '1rem',
                    background: 'linear-gradient(135deg, #2E7D32, #4CAF50)',
                    boxShadow: '0 4px 18px rgba(46,125,50,0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1B5E20, #388E3C)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 22px rgba(46,125,50,0.4)',
                    },
                    transition: 'all 0.25s ease',
                  }}
                >
                  {status === 'sending' ? 'Wird gesendet…' : 'Nachricht senden'}
                </Button>

                <Typography sx={{ color: '#9E9189', fontSize: '0.78rem', lineHeight: 1.6 }}>
                  * Pflichtfelder. Ihre Daten werden ausschließlich zur Beantwortung Ihrer Anfrage verwendet und nicht an Dritte weitergegeben.
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default Kontakt;
